import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTracking } from "../hooks/useTracking";
import { ActionCategory } from "../services/logging/logService";
import axios from "axios";
import { Field, Form, Formik, FormikProps } from "formik";
import { useSelector } from "react-redux";
import moment from "moment";
import Select from "../components/Select";
import BeatLoader from "react-spinners/BeatLoader";
import { FaDownload } from "react-icons/fa";
import { handleLogout } from "../config/axios";
import FiltersModal from "../components/FiltersModal";
import { savePdf } from "./print/ordini/Ordini";
import classNames from "classnames";
import CloseIcon from "../assets/images/close_icon.png";
import decryptData from "../decrypt";
import { fetchUserInfo, fetchClientsByAgentCodes, getAllAgentCodes, processAgentCodeValue } from "../services/userService";

// Type definitions
interface Agent {
  db2_user_id: string;
  codice_agente: string;
  first_name: string;
  last_name: string;
  raw_codes?: string[];
}

interface OrderFilters {
  data_ordine: string[];
  agenti: string[];
  evaso: string;
  ritiro_cliente: string;
  numero_ordine?: string;
  codice_cliente?: string;
  ragione_sociale?: string;
  tipologia_consegna?: string;
  tipo_consegna?: string;
  codice_agente?: string;
  order_by?: Array<{ field: string; column?: string; label?: string; mode?: string }>;
  from?: string;
  to?: string;
  searchFilters?: Array<{ codice_cliente: string; ragione_sociale: string }>; // Aggiunto qui
}


interface ExtendedOrderFilters extends OrderFilters {
  page?: number;
  pageSize?: number;
  campo_ordinamento?: string;
  tipo_ordinamento?: string;
}

interface OrderSummary {
  SOMMA_IMPORTO: number;
  SOMMA_RAE: number;
  quantita_ordini: number;
}

interface FormValues {
  citta: string;
  cap: string;
  ritiro_cliente: string;
  tipologia_consegna: string;
  search: any;
  order_by: any[];
  vista: string;
  from: string;
  to: string;
  evaso: string;
  numero_ordine: string;
  oneProduct: boolean;
  codice_cliente?: string;
  agenti: string[];
}

/**
 * Funzione per registrare direttamente le azioni di ricerca senza usare il sistema di debounce
 */
const logSearchDirectly = (action: string, description: string, metadata: any) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No auth token found, cannot log search');
      return;
    }
    
    // Crea un payload di log simile a quello usato dal logService, ma con ID univoco
    const logMessage = {
      action: `search:${action}`,
      description,
      timestamp: new Date().toISOString(),
      pagePath: window.location.pathname,
      sessionId: `direct_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      metadata: {
        ...metadata,
        directLog: true,
        timestamp: new Date().toISOString()
      }
    };
    
    // Payload completo per l'API
    const payload = {
      level: 'info',
      serviceName: 'cediFE',
      message: logMessage,
    };
    
    // Chiamata diretta all'API senza debounce
    axios.post('/nodeapi/writeLog', payload, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    .then(() => {
      console.log(`Log di ricerca inviato direttamente: ${action}`);
    })
    .catch(error => {
      console.error('Errore nell\'invio diretto del log:', error);
    });
  } catch (error) {
    console.error('Errore nella preparazione del log diretto:', error);
  }
};

export default function Ordini() {
  const navigate = useNavigate();
  const tracking = useTracking();
  const tableRef = useRef<HTMLDivElement>(null);
  const formikRef = useRef<FormikProps<FormValues>>(null);
  const initialLoadDone = useRef(false);
  
  // State variables
  const [isInitializing, setIsInitializing] = useState(true);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [userRole, setUserRole] = useState("");
  const [currentAgentID, setCurrentAgentID] = useState("");
  const [agentFilters, setAgentFilters] = useState<Agent[]>([]);
  const [filters, setFilters] = useState<Partial<OrderFilters>>({});
  const [columns, setColumns] = useState<string[]>([
    "ragione_sociale",
    "data_ordine"
  ]);
  const [orderByColumns, setOrderByColumns] = useState<any[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [itemsPerPage] = useState(100);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [documentToDownload, setDocumentToDownload] = useState<any>(null);
  const [downloadedFiles, setDownloadedFiles] = useState<Record<string, string>>({});
  const [sum, setSum] = useState<OrderSummary>({
    SOMMA_IMPORTO: 0,
    SOMMA_RAE: 0,
    quantita_ordini: 0,
  });

  // Get theme from Redux store
  const theme = useSelector((state: any) => state.menu.theme) || "light";

  // Early initialization of user data from localStorage
  const [userFromStorage] = useState(() => {
    try {
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        // Set initial role from localStorage
        if (user && user.profile === "SUPERVISOR") {
          setUserRole("supervisor");
        }
        // Set initial agent ID
        if (user && user.db2_user_id) {
          setCurrentAgentID(user.db2_user_id);
        }
        return user;
      }
    } catch (e) {
      console.error("Failed to parse user data from localStorage");
    }
    return null;
  });

  // Fetch agents data from API
  const fetchAgents = async () => {
    try {
      const { user, isSupervisor, agentCodes, agents } = await fetchUserInfo();
      
      if (!user) {
        console.error("Unable to fetch user info");
        return { agents: [], agentCodes: [], role: "", isSupervisor: false, currentAgentCode: "" };
      }
      
      const role = isSupervisor ? "supervisor" : "agent";
      setUserRole(role);
      
      // Set current agent ID if available (use first code for display)
      const currentAgentCode = agentCodes.length > 0 ? agentCodes[0] : "";
      if (currentAgentCode) {
        setCurrentAgentID(currentAgentCode);
        console.log("Current agent code:", currentAgentCode);
      }
      
      // Format agents for the component using consistent structure
      const formattedAgents = agents.map(agent => ({
        // Store as JSON string to maintain consistent format
        db2_user_id: JSON.stringify(agent.all_codes),
        codice_agente: agent.all_codes[0] || "", // First code for display
        first_name: agent.first_name,
        last_name: agent.last_name,
        raw_codes: agent.all_codes // Store raw array for direct access
      }));
      
      // For regular agents, create an agent entry if none exists
      if (!isSupervisor && formattedAgents.length === 0 && agentCodes.length > 0) {
        formattedAgents.push({
          db2_user_id: JSON.stringify(agentCodes),
          codice_agente: agentCodes[0],
          first_name: user.firstName || "",
          last_name: user.lastName || "",
          raw_codes: agentCodes
        });
      }
      
      console.log(`Utente ${role} con codici agente:`, agentCodes);
      setAgentFilters(formattedAgents);
      
      return {
        agents: formattedAgents,
        agentCodes,
        role,
        isSupervisor,
        currentAgentCode
      };
    } catch (error) {
      console.error("Error fetching agents:", error);
      return {
        agents: [],
        agentCodes: [],
        role: "",
        isSupervisor: false,
        currentAgentCode: ""
      };
    }
  };
  
  // Fetch filters for customer search
  const fetchFilters = async (agentCodes?: string[]) => {
    try {
      setLoading(true);
      
      // Use centralized function to get all agent codes if not provided
      let finalAgentCodes: string[] = [];
      
      if (Array.isArray(agentCodes) && agentCodes.length > 0) {
        // Use provided agent codes
        finalAgentCodes = agentCodes;
      } else {
        // Get all agent codes using the centralized function
        finalAgentCodes = await getAllAgentCodes();
      }
      
      // Ensure we have agent codes to work with
      if (finalAgentCodes.length === 0) {
        console.error("No agent codes available for filters");
        finalAgentCodes = ["UNKNOWN"]; // Fallback value
      }
      
      console.log("Using agent codes for client filters:", finalAgentCodes);
      
      // Use the centralized service to fetch clients
      const clients = await fetchClientsByAgentCodes(finalAgentCodes);
      
      // Map clients to the expected format
      const mappedFilters = Array.isArray(clients)
        ? clients.map((item: any) => ({
            codice_cliente: item.CODICE_CLIENTE,
            ragione_sociale: item.RAGIONE_SOCIALE,
          }))
        : [];
      
      setFilters({ searchFilters: mappedFilters });
    } catch (e) {
      console.error("Error in fetchFilters:", e);
    } finally {
      setLoading(false);
    }
  };
  

  // Get agent list based on role and selection
  const getAgentList = async (isSupervisor: boolean, storedAgents?: string[]): Promise<string[]> => {
    // Use stored agents if provided and not empty
    if (storedAgents && storedAgents.length > 0) {
      console.log("Using stored agent codes:", storedAgents);
      return storedAgents;
    }
    
    // Use centralized function to get all relevant agent codes
    const allAgentCodes = await getAllAgentCodes();
    console.log("Retrieved all agent codes:", allAgentCodes);
    
    return allAgentCodes;
  };

  // Utility function to get date range for the last 8 months
  const getLastEightMonthsDates = () => {
    const endDate = moment().format("YYYY-MM-DD");
    const startDate = moment().subtract(8, "months").format("YYYY-MM-DD");
    return {
      from: startDate,
      to: endDate,
    };
  };

  // Main function to fetch orders data
  const fetchOrders = async (filters: any = {}, pageNumber?: number) => {
    setLoading(true);
    try {
      // Default date range
      const defaultFrom = moment().subtract(8, "months").format("YYYYMMDD");
      const defaultTo = moment().format("YYYYMMDD");

      // Determine sorting field and direction
      let orderField = "data_ordine";
      let orderDirection = "DESC";

      if (filters.order_by && filters.order_by.length > 0) {
        const lastOrder = filters.order_by[filters.order_by.length - 1];
        orderField = lastOrder.column || lastOrder.field;
        orderDirection = lastOrder.mode;
      }

      // Determine if user is supervisor
      const isSupervisor = userRole === "supervisor" || userFromStorage?.profile === "SUPERVISOR";
      
      // Get agent codes for the request
      let agentList: string[] = [];
      
      if (filters.agenti && Array.isArray(filters.agenti) && filters.agenti.length > 0) {
        // Use agent codes from filters
        console.log("Using agent codes from filters:", filters.agenti);
        agentList = filters.agenti;
      } else {
        // Get all agent codes using our centralized function
        agentList = await getAllAgentCodes();
        console.log("Using all agent codes from getAllAgentCodes:", agentList);
      }

      // Build default filters
      const defaultFilters: OrderFilters = {
        data_ordine: [defaultFrom, defaultTo],
        agenti: agentList,
        evaso: filters.evaso || "No",
        ritiro_cliente: filters.ritiro_cliente || "Tutti",
        tipologia_consegna: filters.tipologia_consegna || "Tutti",
      };

      // Combine default with provided filters
      let finalFilters: ExtendedOrderFilters = { ...defaultFilters };

      // Format dates if provided in filters
      if (filters.from || filters.to) {
        const fromDate = filters.from
          ? filters.from.includes("-")
            ? filters.from.replace(/-/g, "")
            : filters.from
          : defaultFrom;
        const toDate = filters.to
          ? filters.to.includes("-")
            ? filters.to.replace(/-/g, "")
            : filters.to
          : defaultTo;
        finalFilters.data_ordine = [fromDate, toDate];
      }

      // Add other filter parameters if provided
      if (filters.numero_ordine) {
        finalFilters.numero_ordine = filters.numero_ordine;
      }
      if (filters.codice_cliente) {
        finalFilters.codice_cliente = filters.codice_cliente;
      }
      if (filters.ragione_sociale) {
        finalFilters.ragione_sociale = filters.ragione_sociale;
      }
      if (filters.tipo_consegna || filters.tipologia_consegna) {
        finalFilters.tipologia_consegna = filters.tipo_consegna || filters.tipologia_consegna;
      }

      // Build the payload including pagination and sorting
      const payload = {
        ...finalFilters,
        page: pageNumber || currentPage,
        pageSize: itemsPerPage,
        campo_ordinamento: orderField,
        tipo_ordinamento: orderDirection,
        codice_cliente: filters.codice_cliente, // ora sarà correttamente valorizzato
        un_prodotto: filters.oneProduct === true, // Aggiungi il campo un_prodotto
      };
      
      // Double-check agent array is never empty (use centralized function as fallback)
      if (!payload.agenti || !payload.agenti.length) {
        console.warn("Agent array is empty, using getAllAgentCodes as fallback");
        payload.agenti = await getAllAgentCodes();
      }

      console.log("Sending orders request with payload:", JSON.stringify(payload));


      // Make the API request
      const response = await fetch("/nodeapi/ordini", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });



       if (response.status !== 200 && response.status !== 201) {
          handleLogout(response, "/nodeapi/ordini", JSON.stringify(payload));
         return;
       }

      const result = await response.json();
      const decrypted = decryptData(
        result.encryptedData,
        result.encryptedKey,
        result.iv
      );
      const decryptedJson = JSON.parse(decrypted);
      console.log("Decrypted orders response:", decryptedJson);

      if (decryptedJson.status === "success") {
        const orders = decryptedJson.data;
        setData(orders);

        // Update pagination state
        if (decryptedJson.pagination) {
          setCurrentPage(decryptedJson.pagination.currentPage);
          setHasMorePages(decryptedJson.pagination.hasMorePages);
        }

        // Calculate totals
        if (orders && orders.length > 0) {
          const totals = {
            SOMMA_IMPORTO: orders.reduce(
              (acc: number, order: any) => acc + (Number(order.SOMMA_IMPORTO) || 0),
              0
            ),
            SOMMA_RAE: orders.reduce(
              (acc: number, order: any) => acc + (Number(order.SOMMA_RAE) || 0),
              0
            ),
            quantita_ordini: orders.length,
          };
          setSum(totals);
        } else {
          setSum({
            SOMMA_IMPORTO: 0,
            SOMMA_RAE: 0,
            quantita_ordini: 0,
          });
        }
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setData([]);
      setSum({
        SOMMA_IMPORTO: 0,
        SOMMA_RAE: 0,
        quantita_ordini: 0,
      });
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    // Esegui solo se abbiamo agenti disponibili
    if (agentFilters.length > 0) {
      try {
        // Cerca i filtri salvati
        const savedFilters = sessionStorage.getItem("ordini-filters");
        if (savedFilters) {
          const parsedFilters = JSON.parse(savedFilters);
          
          // Se c'è un agente selezionato o un array di agenti con un solo elemento
          if (parsedFilters.selectedAgent || 
             (parsedFilters.agenti && parsedFilters.agenti.length === 1)) {
            
            // Usa selectedAgent se disponibile, altrimenti usa il primo elemento dell'array agenti
            const savedAgentId = parsedFilters.selectedAgent?.value || parsedFilters.agenti[0];
            
            // Trova l'agente corrispondente nella lista degli agenti disponibili
            const matchingAgent = agentFilters.find(agent => 
              agent.db2_user_id === savedAgentId || agent.codice_agente === savedAgentId
            );
            
            if (matchingAgent) {
              // Crea l'oggetto option per il dropdown
              const agentOption = {
                value: matchingAgent.db2_user_id,
                label: `${matchingAgent.first_name} ${matchingAgent.last_name}`
              };
              
              console.log("Ripristinando agente selezionato:", agentOption);
              setSelectedAgent(agentOption);
            }
          }
        }
      } catch (error) {
        console.error("Errore nel ripristino dell'agente selezionato:", error);
      }
    }
  }, [agentFilters]); 

  useEffect(() => {
    const initializeData = async () => {
      if (initialLoadDone.current) return;
  
      try {
        setIsDataLoading(true);
        setIsInitializing(true);
  
        // Ottieni dati agenti e ruolo
        const { agents, agentCodes, role, isSupervisor, currentAgentCode } = await fetchAgents();
        
        // Use the centralized function to get ALL agent codes including subordinates
        const allAgentCodes = await getAllAgentCodes();
        console.log("Using getAllAgentCodes for initial filters:", allAgentCodes);
        
        // Usa i codici agente completi per fetchFilters
        await fetchFilters(allAgentCodes);
  
        // Recupera i filtri salvati
        const storedFilters = sessionStorage.getItem("ordini-filters");
        const parsedFilters = storedFilters ? JSON.parse(storedFilters) : {};
  
        // Get default date range
        const { from, to } = getLastEightMonthsDates();
  
        // Get agent codes from stored filters or get all agent codes
        let initialAgentCodes: string[];
        if (parsedFilters.agenti && parsedFilters.agenti.length > 0) {
          initialAgentCodes = parsedFilters.agenti;
        } else {
          // Use the centralized function to get all agent codes
          initialAgentCodes = await getAllAgentCodes();
        }
        
        // Check if there's a saved customer search
        const hasCustomerSearch = parsedFilters.search || parsedFilters.codice_cliente;
        
        // Prepara request data iniziale
        const initialRequestData: OrderFilters = {
          data_ordine: [parsedFilters.from || from, parsedFilters.to || to],
          from: parsedFilters.from || from,
          to: parsedFilters.to || to,
          evaso: parsedFilters.evaso || "No",
          ritiro_cliente: parsedFilters.ritiro_cliente || "Tutti",
          tipologia_consegna: parsedFilters.tipologia_consegna || "Tutti",
          agenti: initialAgentCodes,
          order_by: parsedFilters.order_by || [],
        };
        
        // If there's a saved customer, add it to the request
        if (hasCustomerSearch) {
          // Add customer info from saved filters
          if (parsedFilters.search) {
            try {
              const searchData = JSON.parse(parsedFilters.search.value);
              initialRequestData.codice_cliente = searchData.codice_cliente;
              initialRequestData.ragione_sociale = searchData.ragione_sociale;
            } catch (e) {
              console.error("Error parsing saved customer data:", e);
            }
          } else if (parsedFilters.codice_cliente) {
            initialRequestData.codice_cliente = parsedFilters.codice_cliente;
          }
          
          console.log("Found saved customer search, will auto-execute search");
          
          // Log the automatic search action
          tracking.trackSearch('Ricerca Ordini Automatica ' + new Date().getTime(), undefined, {
            filters: initialRequestData,
            userRole: role,
            autoSearch: true,
            savedCustomer: true,
            timestamp: new Date().toISOString()
          });
          
          // Also log with direct method
          logSearchDirectly('ordini_auto_search', 'Ricerca Ordini Automatica con Cliente Salvato', {
            filters: initialRequestData,
            userRole: role,
            autoSearch: true,
            savedCustomer: true,
            timestamp: new Date().toISOString()
          });
        }
  
        console.log("Initial request data:", initialRequestData);
  
        // Fetch orders - this will now include customer filter if present
        await fetchOrders(initialRequestData);
  
        initialLoadDone.current = true;
      } catch (error) {
        console.error("Error initializing data:", error);
        setData([]);
        setSum({
          SOMMA_IMPORTO: 0,
          SOMMA_RAE: 0,
          quantita_ordini: 0,
        });
  
        if ((error as any).status === 401 || (error as any).status === 403) {
          handleLogout();
        }
      } finally {
        setIsInitializing(false);
        setIsDataLoading(false);
      }
    };
  
    initializeData();
  }, []);
  // Filter orders based on form submission
  const filterOrders = async (values: FormValues) => {
    try {
      setCurrentPage(1);

      const filtersToSave = {
        ...values,
        codice_cliente: values.codice_cliente, // aggiunto esplicitamente
        selectedAgent,
        pagination: {
          page: 1,
          per_page: itemsPerPage,
        },
      };
      sessionStorage.setItem("ordini-filters", JSON.stringify(filtersToSave));
      
      // 1. Log search action with the standard tracking system
      tracking.trackSearch('Ricerca Ordini ' + new Date().getTime(), undefined, {
        filters: filtersToSave,
        userRole,
        timestamp: new Date().toISOString()
      });
      
      // 2. Log search action with our direct method
      logSearchDirectly('ordini_search', 'Ricerca Ordini (Direct Log)', {
        filters: filtersToSave,
        userRole,
        timestamp: new Date().toISOString()
      });

      setLoading(true);
      setData([]);

      // Extract customer info from search if present
      let customerInfo = {};
      if (values.search) {
        try {
          const searchData = JSON.parse(values.search.value);
          customerInfo = {
            codice_cliente: searchData.codice_cliente,
            ragione_sociale: searchData.ragione_sociale,
          };
        } catch (e) {
          console.error("Error parsing customer data:", e);
        }
      }

      // Determine if user is supervisor
      const isSupervisor = userRole === "supervisor" || userFromStorage?.profile === "SUPERVISOR";
      
      // Get agent codes - use values.agenti if provided, otherwise get all agent codes
      let agentCodes: string[];
      
      if (values.agenti && values.agenti.length > 0) {
        // Use agent codes from form values
        agentCodes = values.agenti;
      } else if (selectedAgent) {
        // Use selected agent's codes
        agentCodes = processAgentCodeValue(selectedAgent.value);
      } else {
        // Get all agent codes from centralized function
        agentCodes = await getAllAgentCodes();
      }
      
      console.log("Using agent codes for filter:", agentCodes);

      const filtriDaApplicare = {
        ...values,
        ...customerInfo,
        agenti: agentCodes,
        oneProduct: values.oneProduct, // Assicura che oneProduct sia passato
        pagination: {
          page: 1,
          per_page: itemsPerPage,
        }
      };

      console.log("Applying filters:", filtriDaApplicare);

      await fetchOrders(filtriDaApplicare, 1);
    } catch (e) {
      console.error("Error filtering orders:", e);
    } finally {
      setLoading(false);
    }
  };

  // Clear all filters
  const clearAllFilters = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }

    // 1. Log clear filters action with standard system
/*     tracking.logAction(
      ActionCategory.USER_ACTION,
      "clear_filters",
      "User cleared all filters in Ordini",
      {
        userRole,
        timestamp: new Date().toISOString()
      }
    ); */

    // 2. Log clear filters with direct method
    logSearchDirectly('ordini_clear_filters', 'Reset Filtri Ordini (Direct Log)', {
      userRole,
      timestamp: new Date().toISOString()
    });

    // Reset selected agent
    setSelectedAgent(null);
    setCurrentPage(1);

    // Get all agent codes using the centralized function
    const allAgentCodes = await getAllAgentCodes();

    // Reset form values
    if (formikRef.current) {
      const { from, to } = getLastEightMonthsDates();
      
      formikRef.current.resetForm({
        values: {
          agenti: allAgentCodes,
          citta: "",
          cap: "",
          ritiro_cliente: "Tutti",
          tipologia_consegna: "Tutti",
          search: null,
          order_by: [],
          vista: "lista",
          from,
          to,
          evaso: "No",
          numero_ordine: "",
          oneProduct: false,
        },
      });
    }

    // Remove saved filters
    sessionStorage.removeItem("ordini-filters");
  };

  // Clear specific filters
  const clearFiltri = (e: React.MouseEvent, setFieldValue: Function) => {
    e.preventDefault();
    setFieldValue("ritiro_cliente", "Tutti");
    setFieldValue("tipologia_consegna", "Tutti");
    setFieldValue("evaso", "No");
    setFieldValue("numero_ordine", "");
    setFieldValue("oneProduct", false);
    setCurrentPage(1);

    // Update form pagination
    setFieldValue("pagination", {
      page: 0,
      per_page: itemsPerPage,
    });
  };

  // Clear order by columns
  const clearOrderBy = (e: React.MouseEvent, setFieldValue: Function) => {
    e.preventDefault();
    setOrderByColumns([]);
    setFieldValue("order_by", []);
  };

  // Handle search change
  const handleSearchChange = (value: any, setFieldValue: Function) => {
    setFieldValue("search", value);
    setCurrentPage(1);
  
    if (value) {
      try {
        const searchData = JSON.parse(value.value);
        setFieldValue("codice_cliente", searchData.codice_cliente);
      } catch (e) {
        console.error("Errore nel parsing del cliente selezionato:", e);
        setFieldValue("codice_cliente", undefined);
      }
    } else {
      setFieldValue("codice_cliente", undefined);
    }
  };
  

  // Handle order by change
  const handleOrderByChange = (newOrderByColumn: any) => {
    setCurrentPage(1);
    setOrderByColumns((previousOrderByColumns) => {
      const orderByColumn = previousOrderByColumns.find(
        (previousOrderByColumn) =>
          previousOrderByColumn.column === newOrderByColumn.value
      );
      if (!orderByColumn) {
        const newOrderByColumns = [
          ...previousOrderByColumns,
          {
            column: newOrderByColumn.value,
            field: newOrderByColumn.value,
            label: newOrderByColumn.label,
            mode: "ASC",
            opened: false,
          },
        ];

        // Update form values
        if (formikRef.current) {
          formikRef.current.setFieldValue("order_by", newOrderByColumns);
        }

        return newOrderByColumns;
      }
      return previousOrderByColumns;
    });
  };

  // Change order direction
  const changeOrderByItemOrder = (orderByItem: any) => {
    setOrderByColumns((previousOrderByColumns) => {
      const orderByColumn = previousOrderByColumns.find(
        (previousOrderByColumn) =>
          previousOrderByColumn.column === orderByItem.column
      );
      const orderByColumnIndex = previousOrderByColumns.findIndex(
        (previousOrderByColumn) =>
          previousOrderByColumn.column === orderByItem.column
      );
      if (orderByColumn) {
        previousOrderByColumns.splice(orderByColumnIndex, 1);
        return [
          ...previousOrderByColumns,
          {
            ...orderByColumn,
            mode: orderByColumn.mode === "ASC" ? "DESC" : "ASC",
          },
        ];
      }
      return previousOrderByColumns;
    });
  };

  // Handle file download
  const handleDownload = async (document: any, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setDocumentToDownload(document);
  };

  // Fetch file content
  const fetchFile = async (document: any) => {
    try {
      const body = {
        cartella: "CONF_ORD_CLI",
        anno: document.ANNO_ORDINE.toString(),
        numero_documento: document.NUMERO_ORDINE.toString().padStart(6, "0"),
        registro: "",
        bollettario: "",
        data_documento: document.DATA_ORDINE.toString(),
      };

      const response = await fetch("/nodeapi/files", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(body),
      });


             if (response.status !== 200 && response.status !== 201) {
          handleLogout(response, "/nodeapi/files", JSON.stringify(body));
         return;
       }

      const result = await response.json();
      const decrypted = decryptData(
        result.encryptedData,
        result.encryptedKey,
        result.iv
      );
      const decryptedJson = JSON.parse(decrypted);

      if (decryptedJson.status === "processed" && decryptedJson.queryResult.fileContent) {
        return decryptedJson.queryResult.fileContent;
      }
      
      return null;
    } catch (error) {
      console.error("Error fetching file:", error);
      return null;
    }
  };

  // Helper function to detect mobile devices
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.matchMedia && window.matchMedia("(max-width: 768px)").matches);
  };

  // Helper function to download file with mobile compatibility
  const downloadFileWithMobileSupport = (base64Data: string, fileName: string) => {
    try {
      // Convert base64 to blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create blob URL
      const blobUrl = URL.createObjectURL(blob);
      
      if (isMobileDevice()) {
        // For mobile devices, use window.open which is more reliable
        const newWindow = window.open(blobUrl, '_blank');
        if (!newWindow) {
          // If popup blocked, fallback to creating a link
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          link.style.display = 'none';
          document.body.appendChild(link);
          
          // Dispatch a click event instead of calling click()
          const clickEvent = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true
          });
          link.dispatchEvent(clickEvent);
          
          // Clean up after a delay
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          }, 100);
        } else {
          // Clean up blob URL after a delay
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 1000);
        }
      } else {
        // Desktop approach
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 100);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback to data URL approach
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${base64Data}`;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Handle document download effect
  useEffect(() => {
    if (documentToDownload) {
      const downloadFile = async () => {
        const fileKey = `CONF_ORD_CLI_${documentToDownload.NUMERO_ORDINE}`;
        const fileName = `${fileKey}.pdf`;
        
        try {
          // Use cached file if available
          if (downloadedFiles[fileKey]) {
            downloadFileWithMobileSupport(downloadedFiles[fileKey], fileName);
            return;
          }

          // Otherwise fetch the file
          const fileContent = await fetchFile(documentToDownload);
          if (fileContent) {
            setDownloadedFiles((prev) => ({ ...prev, [fileKey]: fileContent }));
            downloadFileWithMobileSupport(fileContent, fileName);
          } else {
            console.error("Error: Invalid or empty file content");
          }
        } catch (error) {
          console.error("Error in download process:", error);
        }
      };

      downloadFile();
      // Reset documentToDownload after a small delay to ensure the download process starts
      setTimeout(() => {
        setDocumentToDownload(null);
      }, 100);
    }
  }, [documentToDownload, downloadedFiles]);

  // Pagination component
  const Pagination = () => {
    const handlePageChange = async (newPage: number) => {
      if (loading) return;
      if (newPage < 1) return;
      if (newPage > currentPage && !hasMorePages) return;

      setCurrentPage(newPage);
      await fetchOrders(formikRef.current?.values || {}, newPage);
    };

    return (
      <div className="flex justify-center items-center py-4">
        <div className="flex items-center">
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1 || loading}
            className={`px-3 py-1 mx-1 ${
              theme === "dark"
                ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            } disabled:opacity-50 disabled:cursor-not-allowed rounded-l-full min-w-[100px]`}
          >
            ««
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            className={`px-3 py-1 mx-1 ${
              theme === "dark"
                ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            } disabled:opacity-50 disabled:cursor-not-allowed rounded`}
          >
            «
          </button>

          {currentPage > 2 && (
            <span className="px-3 py-1 mx-1 text-gray-500">...</span>
          )}

          {currentPage > 1 && (
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              className={`px-3 py-1 mx-1 ${
                theme === "dark"
                  ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                  : "bg-gray-200 text-gray-800 hover:bg-gray-300"
              } rounded`}
            >
              {currentPage - 1}
            </button>
          )}

          <span className="px-3 py-1 mx-1 bg-blue-600 text-white rounded">
            {currentPage}
          </span>

{hasMorePages && (
  <>
    <button
      onClick={() => handlePageChange(currentPage + 1)}
      className={`px-3 py-1 mx-1 ${
        theme === "dark"
          ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
          : "bg-gray-200 text-gray-800 hover:bg-gray-300"
      } rounded`}
      disabled={loading} // Disabilita durante il caricamento
    >
      {currentPage + 1}
    </button>
    <span className="px-3 py-1 mx-1 text-gray-500">...</span>
  </>
)}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!hasMorePages || loading}
            className={`px-3 py-1 mx-1 ${
              theme === "dark"
                ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                : "bg-gray-200 text-gray-800 hover:bg-gray-300"
            } disabled:opacity-50 disabled:cursor-not-allowed rounded`}
          >
            »
          </button>
        </div>
      </div>
    );
  };

  // Helper function to convert deposit code to name
  const switchDeposito = (deposito: string) => {
    switch (deposito) {
      case "C1": return "CASORIA";
      case "D1": return "CASERTA";
      case "U1": return "PESARO";
      case "T1": return "BARBERINO";
      case "P1": return "PESCARA";
      default: return "CASORIA";
    }
  };

  // Get initial values for Formik
  const getInitialValues = (): FormValues => {
    const storedFiltersStr = sessionStorage.getItem("ordini-filters");
    const storedFilters = storedFiltersStr && storedFiltersStr !== "{}" 
      ? JSON.parse(storedFiltersStr)
      : {};
    
    const { from, to } = getLastEightMonthsDates();
    const isSupervisor = userRole === "supervisor" || userFromStorage?.profile === "SUPERVISOR";
    
    // Use stored agent codes if available
    const agentCodes = storedFilters.agenti && Array.isArray(storedFilters.agenti) && storedFilters.agenti.length > 0 
      ? storedFilters.agenti 
      : [];
    
    return {
      citta: storedFilters.citta || "",
      cap: storedFilters.cap || "",
      ritiro_cliente: storedFilters.ritiro_cliente || "Tutti",
      tipologia_consegna: storedFilters.tipologia_consegna || "Tutti",
      search: storedFilters.search || null,
      order_by: orderByColumns,
      vista: storedFilters.vista || "lista",
      from: storedFilters.from || from,
      to: storedFilters.to || to,
      evaso: storedFilters.evaso || "No",
      numero_ordine: storedFilters.numero_ordine || "",
      oneProduct: storedFilters.oneProduct || false,
      agenti: agentCodes,
      codice_cliente: storedFilters.codice_cliente || undefined,
    };
  };

  // Refetch orders when current page changes
  const handlePageChange = async (newPage: number) => {
    if (loading) return;
    if (newPage < 1) return;
    if (newPage > currentPage && !hasMorePages) return;
  
    setCurrentPage(newPage);
    
    // Ensure we have the form values
    const currentFilters = formikRef.current?.values || {};
    
    // Type assertion for currentFilters
    const typedFilters = currentFilters as OrderFilters;
  
  // Process agent codes if needed
  if (typedFilters.agenti && typedFilters.agenti.length === 0) {
      // Get all agent codes from the service if none are specified
      const allAgentCodes = await getAllAgentCodes();
      typedFilters.agenti = allAgentCodes;
    }
    
    // Assicuriamoci che il loading sia gestito correttamente
    try {
      setLoading(true);
      await fetchOrders(typedFilters, newPage);
    } catch (error) {
      console.error("Error changing page:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize={true}
      initialValues={getInitialValues()}
      validateOnChange={false}
      onSubmit={filterOrders}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
      }) => (
        <Form>
          <div className="py-8 pb-24">
            {/* Show loader if data is still loading */}
            {isDataLoading || isInitializing || (loading && currentPage === 1) ? (
              <div className="flex justify-center items-center h-screen">
                <BeatLoader color="#2563eb" />
              </div>
            ) : (
              <div>
                {/* Page header with title and PDF button */}
                <div className="py-8">
                  <div className="relative">
                    <div className="sm:max-w-4xl px-5 sm:px-12 mx-auto flex-col justify-between mt-[50px] md:mt-50">
                      <h2 className="hidden sm:block text-4xl text-blue-500 sm:max-w-72 font-bold sm:mb-24">
                        Ordini
                      </h2>
                    </div>

                    {/* PDF export button */}
                    <div className="absolute top-0 right-0 flex flex-col gap-2">
                      <button
                        className="py-2 px-4 inline-flex items-center justify-center gap-x-2 text-xs font-semibold border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none rounded-l-full min-w-[100px]"
                        onClick={async () => {
                          setIsGeneratingPdf(true);
                          
                          try {
                            const filters = formikRef.current?.values || {};
                            
                            // Get all agent codes using the centralized function
                            const allAgentCodes = await getAllAgentCodes();
                            
                            // Use the current filters but with all agent codes
                            await savePdf({
                              ...filters,
                              agenti: allAgentCodes
                            });
                          } catch (error) {
                            console.error("Error generating PDF:", error);
                          } finally {
                            setIsGeneratingPdf(false);
                          }
                        }}
                        type="button"
                        disabled={isGeneratingPdf}
                      >
                        {isGeneratingPdf ? (
                          <div className="flex items-center justify-center">
                            <BeatLoader size={8} color="#ffffff" />
                          </div>
                        ) : (
                          "PDF"
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Search filters section */}
                  <div className="sm:max-w-6xl px-5 sm:px-12 mx-auto flex flex-col justify-between mb-12 mt-[50px] md:mt-0">
                    <div className="flex justify-start md:justify-center items-start gap-3 gap-y-6 md:gap-5 flex-wrap md:mt-[50px] md:mt-0">
                      <h3 className="md:hidden text-4xl text-blue-500 sm:max-w-72 font-bold sm:mb-24 w-[100%]">
                        Ricerca
                      </h3>
                      
                      {/* Agent selector for supervisors */}
                      {(userRole === "supervisor" || userFromStorage?.profile === "SUPERVISOR") && (
                      <div className="w-full md:w-auto">
                      <Select
                      options={agentFilters.map((agent) => ({
                        value: agent.db2_user_id,
                        label: `${agent.first_name} ${agent.last_name}`,
                        raw_codes: agent.raw_codes || [] // Pass raw codes with fallback
                      }))}
                      placeholder="Seleziona Agente"
                      value={selectedAgent}
                      isClearable={true}
                      onChange={async (val) => {
                      setSelectedAgent(val);
                      const agentiDaUsare = val
                      ? processAgentCodeValue(val.value)
                        : agentFilters
                              .flatMap(agent => agent.raw_codes || [])
                                    .filter(Boolean);
                              setFieldValue("agenti", agentiDaUsare);
                              setFieldValue("search", null);
                              setFieldValue("codice_cliente", undefined);
                              await fetchFilters(agentiDaUsare);
                            }}
                            classNames={{
                              container: () => `w-full md:w-auto`,
                              control: () => `cu-m-wm cu-wi-se min-h-c border dark:border-neutral-400`,
                              singleValue: () => `!text-neutral-500 dark:!text-neutral-400`,
                              option: () => `hover:bg-blue-500 hover:text-white`,
                            }}
                          />
                        </div>
                      )}

                      {/* Customer search */}
                      <div className="w-full md:w-auto">
                        <Select
                          classNames={{
                            container: () => `w-full md:w-auto`,
                            control: () => `cu-m-wm cu-wi-se min-h-c border dark:border-neutral-400`,
                            singleValue: () => `!text-neutral-500 dark:!text-neutral-400`,
                            option: () => `hover:bg-blue-500 hover:text-white`,
                          }}
                          onChange={(val) => handleSearchChange(val, setFieldValue)}
                          value={values.search}
                          isClearable={true}
                          placeholder="Cliente / Codice Cliente"
                          options={filters?.searchFilters?.map((sf: any) => ({
                            value: JSON.stringify(sf),
                            label: `${sf.ragione_sociale} (${sf.codice_cliente})`,
                          }))}
                        />
                      </div>
                      
                      {/* Date range inputs */}
                      <Field
                        type="date"
                        name="from"
                        onChange={(e: any) => setFieldValue("from", e?.target.value)}
                        className={classNames({
                          "cu-m-wm cu-m-wm placeholder-current::placeholder px-3 min-h-c block dark:text-neutral-400 border bg-transparent focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none rounded": true,
                          "border-red-600": errors.from,
                        })}
                        placeholder="From Date"
                        style={{
                          colorScheme: theme === "dark" ? "dark" : "light",
                        }}
                      />
                      
                      <Field
                        type="date"
                        name="to"
                        onChange={(e: any) => setFieldValue("to", e?.target.value)}
                        className={classNames({
                          "cu-m-wm placeholder-current::placeholder min-h-c px-3 block dark:text-neutral-400 border bg-transparent focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none rounded": true,
                          "border-red-600": errors.to,
                        })}
                        placeholder="To Date"
                        style={{
                          colorScheme: theme === "dark" ? "dark" : "light",
                        }}
                      />
                      
                      {/* Action buttons */}
                      <div className="w-full flex flex-col md:flex-row gap-2 justify-center">
                        <div className="hidden md:block w-full md:w-auto">
                          <FiltersModal
                            values={values}
                            setFieldValue={setFieldValue}
                            filters={filters}
                            columns={columns}
                            handleOrderByChange={handleOrderByChange}
                            orderByColumns={orderByColumns}
                            changeOrderByItemOrder={changeOrderByItemOrder}
                            clearFiltri={(e) => clearFiltri(e, setFieldValue)}
                            clearOrderBy={(e) => clearOrderBy(e, setFieldValue)}
                          />
                        </div>

                        <button
                          onClick={clearAllFilters}
                          className="w-full md:w-auto block md:inline-block py-2 md:py-2 px-4 bg-blue-600 text-white font-semibold text-center hover:bg-blue-700"
                        >
                          Cancella filtri
                        </button>
                        <button
                          type="submit"
                          className="w-full md:w-auto block md:inline-block py-2 md:py-2 px-4 bg-blue-600 text-white font-semibold text-center hover:bg-blue-700"
                        >
                          Cerca
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Mobile filters section */}
                  <div className="lg:hidden border">
                    <nav
                      className="grid grid-cols-2 space-x-1 divide-neutral-300"
                      aria-label="Tabs"
                      role="tablist"
                    >
                      <div
                        id="basic-tabs-item-1"
                        data-hs-tab="#basic-tabs-1"
                        className="active p-4 flex items-center gap-5 justify-between"
                        aria-controls="basic-tabs-1"
                        role="tab"
                        onClick={() => {
                          const filtersSection = document.getElementById("basic-tabs-1");
                          filtersSection?.classList.toggle("hidden");
                        }}
                      >
                        <div className="flex gap-2">
                          <h4 className="text-blue-600 text-xl">Filtri</h4>
                          <span className="bg-blue-500 group-active:text-blue-500 group-active:bg-white text-md text-white px-1.5 ml-4">
                            5
                          </span>
                        </div>
                        <img
                          src={CloseIcon}
                          className="w-[24px] h-[24px]"
                          onClick={(e) => {
                            e.stopPropagation();
                            const filtersSection = document.getElementById("basic-tabs-1");
                            filtersSection?.classList.add("hidden");
                            clearFiltri(e, setFieldValue);
                          }}
                          alt="Close"
                        />
                      </div>

                      {/* Order by tabs */}
                      <div
                        id="basic-tabs-item-2"
                        data-hs-tab="#basic-tabs-2"
                        className="p-4 flex justify-between items-center gap-5"
                        aria-controls="basic-tabs-2"
                        role="tab"
                        onClick={() => {
                          const orderBySection = document.getElementById("basic-tabs-2");
                          orderBySection?.classList.toggle("hidden");
                        }}
                      >
                        <h4 className="text-blue-600 text-xl">Ordina Per</h4>
                        <img
                          src={CloseIcon}
                          className="w-[24px] h-[24px]"
                          onClick={(e) => {
                            e.stopPropagation();
                            const orderBySection = document.getElementById("basic-tabs-2");
                            orderBySection?.classList.add("hidden");
                            clearOrderBy(e, setFieldValue);
                          }}
                          alt="Close"
                        />
                      </div>
                    </nav>
                  </div>

                  {/* Mobile filters content */}
                  <div>
                    <div
                      id="basic-tabs-1"
                      className="block hidden lg:hidden"
                      role="tabpanel"
                      aria-labelledby="basic-tabs-item-1"
                    >
                      <div className="px-10">
                        <div>
                          {/* Ritiro Cliente filter */}
                          <div>
                            <div className="py-3 flex items-center text-neutral-600 dark:text-white after:flex-[1_1_0%] after:border-t after:border-gray-600 dark:after:border-gray-200 after:ms-6">
                              Ritiro Cliente
                            </div>
                            <div className="flex hover:cursor-pointer">
                              <label
                                htmlFor="ritiro_cliente_attivo"
                                className={classNames({
                                  "hover:cursor-pointer flex justify-center min-h-c p-2 w-full border border-r-0 border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white": true,
                                  "bg-blue-600 text-white": values.ritiro_cliente === "Si",
                                })}
                              >
                                <input
                                  type="radio"
                                  id="ritiro_cliente_attivo"
                                  name="ritiro_cliente"
                                  className="hidden shrink-0 mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                                  onChange={(e: any) => setFieldValue("ritiro_cliente", e?.target.value)}
                                  value="Si"
                                  checked={values.ritiro_cliente === "Si"}
                                />
                                <span>Sì</span>
                              </label>

                              <label
                                htmlFor="ritiro_cliente_no"
                                className={classNames({
                                  "hover:cursor-pointer flex justify-center min-h-c p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white": true,
                                  "bg-blue-600 text-white": values.ritiro_cliente === "No",
                                })}
                              >
                                <input
                                  type="radio"
                                  id="ritiro_cliente_no"
                                  name="ritiro_cliente"
                                  className="hidden shrink-0 mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                                  onChange={(e: any) => setFieldValue("ritiro_cliente", e?.target.value)}
                                  value="No"
                                  checked={values.ritiro_cliente === "No"}
                                />
                                <span>No</span>
                              </label>

                              <label
                                htmlFor="ritiro_cliente_Tutti"
                                className={classNames({
                                  "hover:cursor-pointer flex justify-center min-h-c p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white": true,
                                  "bg-blue-600 text-white": values.ritiro_cliente === "Tutti",
                                })}
                              >
                                <input
                                  type="radio"
                                  id="ritiro_cliente_Tutti"
                                  name="ritiro_cliente"
                                  className="hidden shrink-0 mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                                  onChange={(e: any) => setFieldValue("ritiro_cliente", e?.target.value)}
                                  value="Tutti"
                                  checked={values.ritiro_cliente === "Tutti"}
                                />
                                <span>Tutti</span>
                              </label>
                            </div>
                          </div>

                          {/* Tipologia Consegna filter */}
                          <div>
                            <div className="py-3 flex items-center text-neutral-600 dark:text-white after:flex-[1_1_0%] after:border-t after:border-gray-600 dark:after:border-gray-200 after:ms-6">
                              Tipologia Consegna
                            </div>
                            <div className="flex">
                              <label
                                htmlFor="tipologia_consegna_attivo"
                                className={classNames({
                                  "hover:cursor-pointer flex justify-center min-h-c p-2 w-full border border-r-0 border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white": true,
                                  "bg-blue-600 text-white": values.tipologia_consegna === "Totale",
                                })}
                              >
                                <input
                                  type="radio"
                                  id="tipologia_consegna_attivo"
                                  name="tipologia_consegna"
                                  className="hidden shrink-0 mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                                  onChange={(e: any) => setFieldValue("tipologia_consegna", e?.target.value)}
                                  value="Totale"
                                  checked={values.tipologia_consegna === "Totale"}
                                />
                                <span>Totale</span>
                              </label>

                              <label
                                htmlFor="tipologia_consegna_no"
                                className={classNames({
                                  "hover:cursor-pointer flex justify-center min-h-c p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white": true,
                                  "bg-blue-600 text-white": values.tipologia_consegna === "Parziale",
                                })}
                              >
                                <input
                                  type="radio"
                                  id="tipologia_consegna_no"
                                  name="tipologia_consegna"
                                  className="hidden shrink-0 mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                                  onChange={(e: any) => setFieldValue("tipologia_consegna", e?.target.value)}
                                  value="Parziale"
                                  checked={values.tipologia_consegna === "Parziale"}
                                />
                                <span>Parziale</span>
                              </label>

                              <label
                                htmlFor="tipologia_consegna_Tutti"
                                className={classNames({
                                  "hover:cursor-pointer flex justify-center min-h-c p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white": true,
                                  "bg-blue-600 text-white": values.tipologia_consegna === "Tutti",
                                })}
                              >
                                <input
                                  type="radio"
                                  id="tipologia_consegna_Tutti"
                                  name="tipologia_consegna"
                                  className="hidden shrink-0 mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:checked:bg-blue-500 dark:checked:border-blue-500 dark:focus:ring-offset-gray-800"
                                  onChange={(e: any) => setFieldValue("tipologia_consegna", e?.target.value)}
                                  value="Tutti"
                                  checked={values.tipologia_consegna === "Tutti"}
                                />
                                <span>Tutti</span>
                              </label>
                            </div>
                          </div>

                          {/* Stato ordine evaso filter */}
                          <div>
                            <div className="py-3 flex items-center text-neutral-600 dark:text-white after:flex-[1_1_0%] after:border-t after:border-gray-600 dark:after:border-gray-200 after:ms-6">
                              Stato ordine evaso
                            </div>
                            <div className="flex">
                              <label
                                htmlFor="evaso_si"
                                className={classNames({
                                  "hover:cursor-pointer flex justify-center min-h-c p-2 w-full border border-r-0 border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white": true,
                                  "bg-blue-600 text-white": values.evaso === "Si",
                                })}
                              >
                                <input
                                  type="radio"
                                  id="evaso_si"
                                  name="evaso"
                                  className="hidden shrink-0"
                                  onChange={() => setFieldValue("evaso", "Si")}
                                  value="Si"
                                  checked={values.evaso === "Si"}
                                />
                                <span>Sì</span>
                              </label>

                              <label
                                htmlFor="evaso_no"
                                className={classNames({
                                  "hover:cursor-pointer flex justify-center min-h-c p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white": true,
                                  "bg-blue-600 text-white": values.evaso === "No",
                                })}
                              >
                                <input
                                  type="radio"
                                  id="evaso_no"
                                  name="evaso"
                                  className="hidden shrink-0"
                                  onChange={() => setFieldValue("evaso", "No")}
                                  value="No"
                                  checked={values.evaso === "No"}
                                />
                                <span>No</span>
                              </label>

                              <label
                                htmlFor="evaso_Tutti"
                                className={classNames({
                                  "hover:cursor-pointer flex justify-center min-h-c p-2 w-full border border-gray-200 text-sm focus:border-blue-500 focus:ring-blue-500 dark:border-gray-700 text-neutral-600 dark:text-white": true,
                                  "bg-blue-600 text-white": values.evaso === "Tutti",
                                })}
                              >
                                <input
                                  type="radio"
                                  id="evaso_Tutti"
                                  name="evaso"
                                  className="hidden shrink-0"
                                  onChange={() => setFieldValue("evaso", "Tutti")}
                                  value="Tutti"
                                  checked={values.evaso === "Tutti"}
                                />
                                <span>Tutti</span>
                              </label>
                            </div>
                          </div>

                          {/* Order number input */}
                          <Field
                            name="numero_ordine"
                            onChange={(e: any) => {
                              setFieldValue("numero_ordine", e?.target.value);
                            }}
                            className={classNames({
                              "min-h-c p-2 px-4 mt-[24px] block w-full text-neutral-500 border rounded-none text-lg focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none": true,
                              "border-red-600": errors.numero_ordine,
                              "bg-white dark:bg-neutral-700 dark:text-neutral-200 dark:border-neutral-600": !errors.numero_ordine,
                            })}
                            style={{
                              colorScheme: theme === "dark" ? "dark" : "light",
                            }}
                            placeholder="Inserisci n° ordine"
                          />

                          {/* One product remaining checkbox */}
                          <div className="flex items-center mb-4 mt-6">
                            <input
                              id="default-checkbox"
                              onChange={(e) => setFieldValue("oneProduct", e.target.checked)}
                              type="checkbox"
                              checked={values.oneProduct}
                              value={String(values.oneProduct)}
                              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                            />
                            <label className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                              Ordini 1 articolo rimanente
                            </label>
                          </div>

                          {/* Filter button */}
                          <div>
                            <button
                              type="submit"
                              className="min-h-c p-2 px-5 mt-[24px] cu-n-w items-center gap-x-2 text-xs font-semibold border border-transparent bg-blue-600 text-white hover:bg-blue-700"
                            >
                              Filtra
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Order by panel */}
                    <div
                      id="basic-tabs-2"
                      className="hidden px-10 py-5"
                      role="tabpanel"
                      aria-labelledby="basic-tabs-item-2"
                    >
                      {values.order_by &&
                        values.order_by.map((orderByItem) => (
                          <div className="grid grid-cols-4 border" key={orderByItem.column || orderByItem.field}>
                            <div className="col-span-4 px-3 py-1.5 flex items-center justify-between">
                              <h4>{orderByItem.label}</h4>
                              <div className="flex gap-2">
                                <span
                                  className={classNames({
                                    "material-symbols-outlined cursor-pointer text-xl": true,
                                    "text-neutral-300 dark:text-natural-600": orderByItem.mode !== "DESC",
                                    "text-blue-500 dark:text-blue-400": orderByItem.mode === "DESC",
                                  })}
                                  onClick={() => changeOrderByItemOrder(orderByItem)}
                                >
                                  arrow_upward
                                </span>
                                <span
                                  className={classNames({
                                    "material-symbols-outlined cursor-pointer text-xl": true,
                                    "text-neutral-300 dark:text-natural-600": orderByItem.mode !== "ASC",
                                    "text-blue-500 dark:text-blue-400": orderByItem.mode === "ASC",
                                  })}
                                  onClick={() => changeOrderByItemOrder(orderByItem)}
                                >
                                  arrow_downward
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}

                      <div
                        className={classNames({
                          "dark:border-neutral-50 border-r border-b border border-dotted": true,
                          "border-t": values.order_by.length === 0,
                        })}
                      >
                        <Select
                          onChange={handleOrderByChange}
                          value={""}
                          isTransparent
                          placeholder="+ Ordinamento"
                          options={columns && columns.map((c) => {
                            const labelMap = {
                              "ragione_sociale": "Ragione Sociale",
                              "data_ordine": "Data Ordine"
                            };
                            return {
                              value: c,
                              label: labelMap[c] || c
                            };
                          })}
                        />
                      </div>
                      <div>
                        <button
                          type="submit"
                          className="cu-n-w min-h-c p-2 px-4 mt-6 items-center gap-x-2 text-xs font-semibold border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                        >
                          Filtra
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Orders table */}
                  <div className="space-y-8 px-5 sm:px-12 mx-auto flex flex-col justify-between mb-12 mt-[50px] md:mt-50">
                    <div className="bg-white dark:bg-neutral-800 rounded-lg overflow-hidden cu-sh-b">
                      <div
                        ref={tableRef}
                        className="w-full table-container"
                        style={{
                          height: "calc(200vh - 500px)",
                          overflowY: "auto",
                          overflowX: "auto",
                        }}
                      >
                        <table className="w-full" id="table">
                          <thead>
                            <tr>
                              <th>Dettaglio</th>
                              <th>N° Ordine</th>
                              <th>Rif. Cliente</th>
                              <th>Ragione sociale</th>
                              <th>Data ordine</th>
                              <th>Data consegna richiesta</th>
                              <th>Deposito</th>
                              <th>Ritiro Cliente</th>
                              <th>Tipo Consegna</th>
                              <th>Stato Ordine</th>
                              <th>Importo prodotti</th>
                              <th>Importo RAE</th>
                              <th>Scarica</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.map((s) => (
                              <tr
                                key={`${s.NUMERO_ORDINE}-${s.ANNO_ORDINE}`}
                                className="border-b border-dotted hover:cursor-pointer cu-bo-dark"
                              >
                                                                <td>
                                  <button
                                    onClick={() => navigate(`/ordini/${s.NUMERO_ORDINE}-${s.ANNO_ORDINE}/detail`)}
                                    className="px-2 py-1 text-blue-600 hover:text-blue-800 focus:outline-none"
                                    title="Visualizza dettagli"
                                  >
                                    &#128269;
                                  </button>
                                </td>
                                <td>{s.NUMERO_ORDINE}</td>
                                <td>{s.RIFERIMENTO_CLIENTE}</td>
                                <td>{s.RAGIONE_SOCIALE}</td>
                                <td>
                                  {moment(s.DATA_ORDINE.toString(), "YYYYMMDD").format("DD/MM/YYYY")}
                                </td>
                                <td>
                                  {moment(s.DATA_CONSEGNA_RICHIESTA.toString(), "YYYYMMDD").format("DD/MM/YYYY")}
                                </td>
                                <td>{s.DEPOSITO}</td>
                                <td>{s.RITIRO_CLIENTE}</td>
                                <td>{s.TIPO_CONSEGNA}</td>
                                <td>{s.STATO_ORDINE === "Si" ? "Evaso" : "Non evaso"}</td>
                                <td>
                                  {Number(s.SOMMA_IMPORTO || 0).toLocaleString(
                                    "it-IT",
                                    { style: "currency", currency: "EUR" }
                                  )}
                                </td>
                                <td>
                                  {Number(s.SOMMA_RAE || 0).toLocaleString(
                                    "it-IT",
                                    { style: "currency", currency: "EUR" }
                                  )}
                                </td>
                                <td>
                                  <button
                                    onClick={(e) => handleDownload(s, e)}
                                    className="cu-l-m text-blue-gray-c hover:text-blue-gray-c"
                                    title="Scarica"
                                    type="button"
                                  >
                                    <FaDownload />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {loading && (
                          <div className="text-center py-4">
                            <BeatLoader color="#2563eb" />
                          </div>
                        )}
                      </div>

                      {/* Order summary */}
                      <div className="sticky bottom-0 right-0 bg-white dark:bg-neutral-800 border-t">
                        {/* Mobile layout */}
                        <div className="lg:hidden p-3 space-y-2">
                          <div className="flex justify-between items-center">
                            <p className="text-xs font-bold text-neutral-700 dark:text-neutral-300">
                              IMPORTO PRODOTTI
                            </p>
                            <p className="text-sm tabular-nums font-medium tracking-wider">
                              {Number(sum?.SOMMA_IMPORTO).toLocaleString(
                                "it-IT",
                                {
                                  style: "currency",
                                  currency: "EUR",
                                  maximumFractionDigits: 0,
                                }
                              )}
                            </p>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-xs font-bold text-neutral-700 dark:text-neutral-300">
                              IMPORTO RAE
                            </p>
                            <p className="text-sm tabular-nums font-medium tracking-wider">
                              {Number(sum?.SOMMA_RAE).toLocaleString("it-IT", {
                                style: "currency",
                                currency: "EUR",
                                maximumFractionDigits: 0,
                              })}
                            </p>
                          </div>
                          <div className="flex justify-between items-center pt-2 border-t">
                            <p className="text-xs font-bold text-neutral-700 dark:text-neutral-300">
                              TOTALE ORDINE
                            </p>
                            <p className="text-sm tabular-nums font-medium tracking-wider">
                              {Number(
                                (sum?.SOMMA_IMPORTO || 0) +
                                  (sum?.SOMMA_RAE || 0)
                              ).toLocaleString("it-IT", {
                                style: "currency",
                                currency: "EUR",
                                maximumFractionDigits: 0,
                              })}
                            </p>
                          </div>
                        </div>
                        
                        {/* Desktop layout */}
                        <div className="hidden lg:flex justify-end items-center p-4 gap-12">
                          <div className="text-right">
                            <p className="text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">
                              IMPORTO PRODOTTI
                            </p>
                            <p className="text-base tabular-nums font-medium tracking-wider">
                              {Number(sum?.SOMMA_IMPORTO).toLocaleString(
                                "it-IT",
                                {
                                  style: "currency",
                                  currency: "EUR",
                                  maximumFractionDigits: 0,
                                }
                              )}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">
                              IMPORTO RAE
                            </p>
                            <p className="text-base tabular-nums font-medium tracking-wider">
                              {Number(sum?.SOMMA_RAE).toLocaleString("it-IT", {
                                style: "currency",
                                currency: "EUR",
                                maximumFractionDigits: 0,
                              })}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-neutral-700 dark:text-neutral-300 mb-1">
                              TOTALE ORDINE
                            </p>
                            <p className="text-base tabular-nums font-medium tracking-wider">
                              {Number(
                                (sum?.SOMMA_IMPORTO || 0) +
                                  (sum?.SOMMA_RAE || 0)
                              ).toLocaleString("it-IT", {
                                style: "currency",
                                currency: "EUR",
                                maximumFractionDigits: 0,
                              })}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Pagination */}
                      <Pagination />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
}